import { localeTag, money, tr } from './i18n';

const WEEKDAY_PROBES = [
  '2024-01-07', // domingo
  '2024-01-08',
  '2024-01-09',
  '2024-01-10',
  '2024-01-11',
  '2024-01-12',
  '2024-01-13'
];

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function weekdayFormatter(style: 'long' | 'short'): Intl.DateTimeFormat {
  return new Intl.DateTimeFormat(localeTag(), { weekday: style, timeZone: 'UTC' });
}

export function weekdayLong(index: number): string {
  const iso = WEEKDAY_PROBES[((index % 7) + 7) % 7];
  const [y, m, d] = iso.split('-').map(Number);
  return capitalize(weekdayFormatter('long').format(new Date(Date.UTC(y, m - 1, d))));
}

export function weekdayShort(index: number): string {
  const iso = WEEKDAY_PROBES[((index % 7) + 7) % 7];
  const [y, m, d] = iso.split('-').map(Number);
  const label = weekdayFormatter('short').format(new Date(Date.UTC(y, m - 1, d))).replace(/\.$/, '');
  return capitalize(label);
}

export function weekdayNames(): string[] {
  return WEEKDAY_PROBES.map((_, index) => weekdayLong(index));
}

export const STATUS_KEYS: Record<string, string> = {
  pending: 'Pendente',
  confirmed: 'Confirmado',
  completed: 'Concluído',
  cancelled_by_customer: 'Cancelado pelo cliente',
  cancelled_by_professional: 'Cancelado pelo profissional',
  no_show: 'Não compareceu'
};

export const SUBSCRIPTION_KEYS: Record<string, string> = {
  trialing: 'Em teste gratuito',
  active: 'Assinatura ativa',
  expired: 'Teste encerrado',
  past_due: 'Pagamento pendente',
  canceled: 'Cancelado',
  suspended: 'Suspenso'
};

export const CYCLE_KEYS: Record<string, string> = {
  monthly: 'Mensal',
  semiannual: 'Semestral',
  yearly: 'Anual'
};

export function cycleLabel(cycle: string | null | undefined): string {
  if (!cycle) return '—';
  const key = CYCLE_KEYS[cycle];
  return key ? tr(key) : cycle;
}

export function brl(cents: number | null | undefined): string {
  return money(cents);
}

export function datePt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(localeTag(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'UTC'
  }).format(new Date(Date.UTC(y, m - 1, d)));
}

/** Dia e mês, sem ano — usado em faixas curtas da agenda. */
export function dayMonth(iso: string | null | undefined): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat(localeTag(), { day: '2-digit', month: '2-digit', timeZone: 'UTC' }).format(
    new Date(Date.UTC(y, m - 1, d))
  );
}

export function dateTimePt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return datePt(iso);
  const day = datePt(iso.slice(0, 10));
  const time = new Intl.DateTimeFormat(localeTag(), { hour: '2-digit', minute: '2-digit' }).format(date);
  return `${day} ${time}`;
}

export function monthYear(year: number, month: number): string {
  return capitalize(
    new Intl.DateTimeFormat(localeTag(), { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(
      new Date(Date.UTC(year, month - 1, 1))
    )
  );
}

export function weekdayOfIso(isoDate: string): number {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1).getDay();
}

export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

export function addDaysIso(isoDate: string, days: number): string {
  const [y, m, d] = isoDate.split('-').map(Number);
  const date = new Date(y, (m ?? 1) - 1, (d ?? 1) + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function minutesFromTime(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function statusLabel(status: string): string {
  const key = STATUS_KEYS[status];
  return key ? tr(key) : status;
}

export function subscriptionLabel(status: string | null | undefined): string {
  if (!status) return '—';
  const key = SUBSCRIPTION_KEYS[status];
  return key ? tr(key) : status;
}

export function trialDaysLeft(expiresAt: string | null | undefined): number | null {
  if (!expiresAt) return null;
  const end = Date.parse(expiresAt);
  if (Number.isNaN(end)) return null;
  return Math.max(0, Math.ceil((end - Date.now()) / 86_400_000));
}
